import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import publicApi from '../services/public.api'
import { useLocation } from '../context/LocationContext'
import { useCart } from '../context/CartContext'
import { addToCartAnimated } from '../utils/addToCartAnimated'
import ProductCardQuantity, { isProductHasVariants } from './ProductCardQuantity'
import { useProductCardCart } from '../hooks/useProductCardCart'

const RecentlyViewedOne = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addedProductId, setAddedProductId] = useState(null);
    const { formatPrice, isCasaRegion, priceRegion } = useLocation();
    const { addToCart } = useCart();

    const getProductPrice = (product) => {
        if (isCasaRegion && product.price_casa) {
            return parseFloat(product.price_casa);
        }
        return parseFloat(product.price || 0);
    };

    const getDiscountedBasePrice = (product) => {
        const basePrice = getProductPrice(product);
        const discount = parseFloat(product.discount || 0);
        if (discount > 0) {
            return basePrice * (1 - discount / 100);
        }
        return basePrice;
    };

    const {
        getSelection,
        updateSelection,
        prepareCartItem,
        weightHintId,
        showWeightHint,
        getDisplayPrice,
        getWeightText,
    } = useProductCardCart(getDiscountedBasePrice);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await publicApi.getProductsWithPromotion(priceRegion);
                if (response.success && response.data) {
                    setProducts(response.data.slice(0, 6));
                }
            } catch (error) {
                console.error('Error fetching promotion products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [priceRegion]);

    const getImageUrl = (imagePath) => publicApi.getImageUrl(imagePath);

    const handleAddToCart = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        const built = prepareCartItem(product);
        if (built.error) {
            showWeightHint(product.id);
            return;
        }
        addToCartAnimated({
            event: e,
            product: built.product,
            addToCart,
            quantity: built.quantity,
        });
        setAddedProductId(product.id);
        setTimeout(() => setAddedProductId(null), 1500);
    };

    if (!loading && products.length === 0) {
        return null;
    }

    return (
        <section className="recently-viewed pt-80">
            <div className="container container-lg">
                <div className="border border-gray-100 p-24 rounded-16">
                    <div className="section-heading mb-24">
                        <div className="flex-between flex-wrap gap-8">
                            <h5 className="mb-0">Nos Offres Spéciales</h5>
                            <div className="flex-align gap-16">
                                <Link
                                    to="/shop"
                                    className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                                >
                                    Voir Tous
                                </Link>
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-main" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                        </div>
                    ) : (
                    <div className="row g-12">
                        {products.map((product) => (
                            <div key={product.id} className="col-12 col-sm-6 col-md-4 col-lg-3 col-xxl-2">
                            <div className="product-card h-100 p-16 border border-gray-100 special-offers-product-hover rounded-16 position-relative transition-2">
                                <Link
                                    to={`/product-details/${product.id}`}
                                    className="product-card__thumb special-offers-product-thumb flex-center rounded-8 position-relative"
                                >
                                        <span className="product-card__badge special-offers-badge px-8 py-4 text-sm text-white position-absolute inset-inline-start-0 inset-block-start-0">
                                            -{product.discount}%
                                    </span>
                                    <img
                                            src={getImageUrl(product.image)}
                                            alt={product.name}
                                        className="home-two-product-img"
                                            onError={(e) => {
                                                e.target.src = 'assets/images/thumbs/product-two-img1.png';
                                            }}
                                    />
                                </Link>
                                <div className="product-card__content mt-16">
                                    <span className="text-main-600 bg-main-50 text-sm fw-medium py-4 px-8">
                                            -{product.discount}%
                                    </span>
                                    <h6 className="title text-lg fw-semibold my-16">
                                        <Link
                                            to={`/product-details/${product.id}`}
                                            className="link text-line-2"
                                            tabIndex={0}
                                        >
                                                {product.name}
                                        </Link>
                                    </h6>
                                    <div className="flex-align gap-6">
                                        <div className="flex-align gap-8">
                                            {[...Array(5)].map((_, i) => (
                                                <span key={i} className={`text-15 fw-medium ${i < Math.round(product.rating_average || 0) ? 'text-warning-600' : 'text-gray-300'} d-flex`}>
                                                    <i className="ph-fill ph-star" />
                                                </span>
                                            ))}
                                        </div>
                                        <span className="text-xs fw-medium text-gray-500">{parseFloat(product.rating_average || 0).toFixed(1)}</span>
                                            <span className="text-xs fw-medium text-gray-500">({product.rating_count || 0})</span>
                                    </div>
                                    <span className="special-offers-vendor py-2 px-8 text-xs rounded-pill mt-16">
                                            Groupe Délices Benkirane
                                    </span>
                                    <div className="flex-between align-items-start gap-8 mt-16 mb-16">
                                        <div className="product-card__price">
                                            {!isCasaRegion && (
                                                <span className="text-gray-400 text-md fw-semibold text-decoration-line-through d-block">
                                                    {formatPrice(getProductPrice(product))} DH
                                                </span>
                                            )}
                                            <span className="text-heading text-md fw-semibold product-card-price-line">
                                                <span className="product-card-price-amount">{formatPrice(getDisplayPrice(product))} DH</span>
                                                <span className="text-gray-500 fw-normal product-card-price-unit">/{getWeightText(product) || 'Qty'}</span>
                                            </span>
                                            {weightHintId === product.id && (
                                                <span className="text-danger text-xs fw-medium d-block mt-4">
                                                    Choisissez un poids
                                                </span>
                                            )}
                                        </div>
                                        <ProductCardQuantity
                                            hasVariants={isProductHasVariants(product)}
                                            selection={getSelection(product)}
                                            onChange={(next) => updateSelection(product, next)}
                                            collapsedUntilInteraction
                                        />
                                    </div>
                                    <span className="text-neutral-600">
                                            Livraison <span className="text-main-600">sous 24h</span>
                                    </span>
                                    <button
                                        onClick={(e) => handleAddToCart(e, product)}
                                        disabled={addedProductId === product.id}
                                        className={`product-card__cart special-offers-cart-btn btn py-11 px-24 rounded-8 flex-center gap-8 fw-medium mt-16 w-100 justify-content-center ${
                                            addedProductId === product.id ? 'btn-success' : ''
                                        }`}
                                        style={{
                                            transition: 'all 0.3s ease',
                                            border: 'none',
                                            cursor: addedProductId === product.id ? 'default' : 'pointer'
                                        }}
                                    >
                                        {addedProductId === product.id ? (
                                            <>Ajouté <i className="ph ph-check-circle" /></>
                                        ) : (
                                            <>Ajouter au panier <i className="ph ph-shopping-cart" /></>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        ))}
                    </div>
                    )}
                </div>
            </div>
        </section>

    )
}

export default RecentlyViewedOne
